import { Injectable } from '@angular/core';
import { isEqual } from 'lodash-es';

/**
 * This class provides helpers for basic data conversions.
 */
@Injectable({
    providedIn: 'root',
})
export class DataUtils {
    private constructor() {}

    /**
     * Converts the input to a number, accepts strings and numbers
     * @param input The value to be converted.
     * @returns a number representation of the value or NaN
     */
    public static toNumber(input: number | string): number {
        switch (typeof input) {
            case 'number':
                return input;
            case 'string':
                return parseInt(input, 10);
            default:
                console.error(`DataUtils.toNumber(): Unexpected input of type "${typeof input}"`);
                return input;
        }
    }

    /**
     * Checks whether both arrays are identical.
     * @param arr1 Array to be compared with arr2
     * @param arr2 Array to be compared with arr1
     * @returns true if both arrays are identical, false otherwise
     */
    public static arraysAreIdentical(arr1: unknown[], arr2: unknown[]): boolean {
        if (arr1.length !== arr2.length) return false;
        for (let i = 0, len = arr1.length; i < len; i++) {
            if (arr1[i] !== arr2[i]) {
                return false;
            }
        }
        return true;
    }

    /**
     * Patches an object with the provided partial object.
     * This operation modifies the target object directly.
     *
     * @param target The object to be patched.
     * @param patch The partial object containing the properties to be patched.
     */
    public static patchObject<T>(target: T, patch: Partial<T>): void {
        Object.keys(patch).forEach((key) => {
            const typedKey = key as keyof T;
            const value = patch[typedKey];

            if (value !== undefined) {
                target[typedKey] = value;
            }
        });
    }

    /**
     * Checks if the parameter is a string and not empty
     * @param str The unknown variable to be checked
     * @returns true (type assertion string) if the provided parameter is a not empty string, otherwise false
     */
    public static isNotEmptyString(str: unknown): str is string {
        return typeof str === 'string' && str.length > 0;
    }

    public static isEqual(a: unknown, b: unknown): boolean {
        return isEqual(a, b);
    }

    public static removeNullFields<T extends object>(original: T): Partial<T> {
        return Object.fromEntries(Object.entries(original).filter(([_, value]) => value !== null)) as Partial<T>;
    }

    public static removeNullAndUndefinedFields<T extends object>(original: T): Partial<T> {
        return Object.fromEntries(Object.entries(original).filter(([_, value]) => value != null)) as Partial<T>;
    }

    /**
     * Checks if the parameter is a Date with a valid time
     * @param date The unknown variable to be checked
     * @returns true (type assertion Date) if the provided parameter is a valid Date, otherwise false
     */
    public static isValidDate(date: unknown): date is Date {
        return date instanceof Date && !isNaN(date.getTime());
    }
}
